import { useEffect, useState } from "react";
import { ApiError, PayrollValidationError, projectYearEndForms } from "@/api/client";
import type { PayrollEmployee, YearEndForms } from "@/api/types";
import { PAY_PERIODS_PER_YEAR, TAX_YEAR } from "@/pages/payroll-fixtures";

export type YearEndStatus = "idle" | "loading" | "ready" | "error";

export interface UseYearEndResult {
  status: YearEndStatus;
  forms: YearEndForms | null;
  error: string | null;
}

/**
 * Projects the roster forward into Form 943 and the year's W-2 boxes.
 *
 * Takes the SAME roster the Payroll screen already fetched (usePayrollRoster) rather than
 * refetching: the annual forms and the current run must be two views of one roster, or the
 * screen can show a 943 that disagrees with the payroll the reader just looked at.
 *
 * `employees === null` means the roster is still loading, which is NOT the same as an empty
 * roster — the same distinction usePayrollRun draws, and for the same reason: a confident
 * $0.00 Form 943 for a farm whose roster simply had not arrived is worse than a spinner.
 */
export function useYearEnd(employees: PayrollEmployee[] | null): UseYearEndResult {
  const [status, setStatus] = useState<YearEndStatus>("idle");
  const [forms, setForms] = useState<YearEndForms | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (employees === null) {
      setStatus("loading");
      return;
    }
    if (employees.length === 0) {
      setStatus("ready");
      setForms(null);
      return;
    }

    const controller = new AbortController();
    setStatus("loading");
    setError(null);

    projectYearEndForms(
      {
        payPeriodsToProject: PAY_PERIODS_PER_YEAR,
        employees: employees.map((employee) => ({
          employeeId: employee.id,
          grossWages: employee.grossWages,
          payPeriodsPerYear: PAY_PERIODS_PER_YEAR,
          taxYear: TAX_YEAR,
          filingStatus: "Single",
          // The roster's H-2A badge IS the FICA determination — the same mapping
          // usePayrollRun makes, and the reason this comes back as a 943 rather than a
          // generic payroll total. See Form943Lines' remarks in the API.
          ficaExempt: employee.ficaExemptByLaw,
        })),
      },
      controller.signal,
    )
      .then((result) => {
        setForms(result);
        setStatus("ready");
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        const message =
          cause instanceof PayrollValidationError
            ? Object.values(cause.errors).flat().join(" ")
            : cause instanceof ApiError
              ? cause.message
              : "Could not project the year-end forms.";
        setError(message);
        setStatus("error");
      });

    return () => controller.abort();
  }, [employees]);

  return { status, forms, error };
}
